/**
 * Remark plugin that turns inline `[N]` citation markers in assistant
 * markdown into links, using a caller-supplied citation-index → URL map.
 *
 * Kept dependency-free (no `unist-util-visit` / `mdast` imports, which are
 * only transitive deps of `react-markdown`) by walking the tree with a
 * minimal local node shape. Produced `link` nodes still flow through the
 * existing `rehypeSanitize` pass and the custom `a` renderer, so protocol
 * enforcement and `target="_blank" rel="noopener noreferrer"` are applied
 * downstream.
 */

/** Minimal mdast node shape this plugin reads / mutates. */
type MdastNode = {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
};

/** Remark plugin signature: a factory returning a tree transformer. */
type CitationRemarkPlugin = () => (tree: unknown) => void;

const CITATION_MARKER_PATTERN = /\[(\d+)\]/g;

/**
 * mdast node types that render as anchors. Markers inside their children
 * must be left untouched to avoid emitting nested links — `linkReference`
 * (reference-style links) renders to `<a>` just like `link`.
 */
const LINK_LIKE_NODE_TYPES = new Set(['link', 'linkReference']);

/**
 * Splits a text node's value into alternating text + link nodes wherever
 * a `[N]` marker has a URL in `citationUrlByIndex`. Markers without a
 * mapped URL are left untouched as literal text. Returns the original
 * single text node when nothing matched.
 */
const splitTextNode = (
  value: string,
  citationUrlByIndex: ReadonlyMap<number, string>
): MdastNode[] => {
  const result: MdastNode[] = [];
  const pattern = new RegExp(CITATION_MARKER_PATTERN);
  let lastIndex = 0;
  let match = pattern.exec(value);

  while (match !== null) {
    const url = citationUrlByIndex.get(Number(match[1]));
    if (url !== undefined) {
      if (match.index > lastIndex) {
        result.push({
          type: 'text',
          value: value.slice(lastIndex, match.index),
        });
      }
      result.push({
        type: 'link',
        url,
        children: [{ type: 'text', value: match[0] }],
      });
      lastIndex = match.index + match[0].length;
    }
    match = pattern.exec(value);
  }

  if (result.length === 0) {
    return [{ type: 'text', value }];
  }
  if (lastIndex < value.length) {
    result.push({ type: 'text', value: value.slice(lastIndex) });
  }
  return result;
};

/**
 * Recursively rewrites the children of `node`, linkifying `[N]` markers in
 * descendant text nodes. Text inside existing link-like nodes (`link`,
 * `linkReference`) is left alone to avoid producing invalid nested links.
 */
const transformNode = (
  node: MdastNode,
  insideLink: boolean,
  citationUrlByIndex: ReadonlyMap<number, string>
): void => {
  if (node.children === undefined) {
    return;
  }

  const nextChildren: MdastNode[] = [];
  for (const child of node.children) {
    if (
      child.type === 'text' &&
      !insideLink &&
      typeof child.value === 'string'
    ) {
      nextChildren.push(...splitTextNode(child.value, citationUrlByIndex));
      continue;
    }
    transformNode(
      child,
      insideLink || LINK_LIKE_NODE_TYPES.has(child.type),
      citationUrlByIndex
    );
    nextChildren.push(child);
  }
  node.children = nextChildren;
};

/**
 * Builds a remark plugin that linkifies inline `[N]` citation markers.
 * @param citationUrlByIndex - Map of citation index to its absolute URL.
 *   Only indices present in the map are linkified; an empty map yields a
 *   no-op transformer.
 */
export const createRemarkCitationLinks = (
  citationUrlByIndex: ReadonlyMap<number, string>
): CitationRemarkPlugin => {
  return () => (tree: unknown) => {
    if (citationUrlByIndex.size === 0) {
      return;
    }
    transformNode(tree as MdastNode, false, citationUrlByIndex);
  };
};
